import { useState, useEffect } from "react";
import { Users, Search, Trash2, Pencil, Loader2, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

interface Props {
  profiles: any[];
  onRefresh?: () => void;
}

const roleLabels: Record<string, { label: string; color: string }> = {
  super_admin: { label: "Super Admin", color: "bg-destructive/10 text-destructive border-destructive/20" },
  restaurant_admin: { label: "Admin Restaurante", color: "bg-primary/10 text-primary border-primary/20" },
  support: { label: "Suporte", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  manager: { label: "Gerente", color: "bg-warning/10 text-warning border-warning/20" },
  attendant: { label: "Atendente", color: "bg-success/10 text-success border-success/20" },
  kitchen: { label: "Cozinha", color: "bg-accent/10 text-accent border-accent/20" },
  customer: { label: "Cliente", color: "bg-muted text-muted-foreground border-border" },
};

const AdminUsersTab = ({ profiles, onRefresh }: Props) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [userRoles, setUserRoles] = useState<Record<string, string[]>>({});
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ full_name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      const { data } = await supabase.from("user_roles").select("user_id, role");
      if (data) {
        const map: Record<string, string[]> = {};
        data.forEach((r: any) => {
          if (!map[r.user_id]) map[r.user_id] = [];
          map[r.user_id].push(r.role);
        });
        setUserRoles(map);
      }
    };
    fetchRoles();
  }, []);

  const openEdit = (profile: any) => {
    setEditingUser(profile);
    setEditForm({
      full_name: profile.full_name || "",
      phone: profile.phone || "",
      email: userEmails[profile.user_id] || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const updates: any = {};
      if (editForm.full_name !== (editingUser.full_name || "")) updates.full_name = editForm.full_name;
      if (editForm.phone !== (editingUser.phone || "")) updates.phone = editForm.phone;
      if (editForm.email && editForm.email !== userEmails[editingUser.user_id]) updates.email = editForm.email;

      if (Object.keys(updates).length === 0) {
        toast({ title: "Nenhuma alteração detectada" });
        setEditingUser(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke("admin-manage-users", {
        body: { action: "update_user", user_id: editingUser.user_id, updates },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Usuário atualizado com sucesso" });
      setEditingUser(null);
      onRefresh?.();
    } catch (err: any) {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile: any) => {
    setDeleting(profile.user_id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-manage-users", {
        body: { action: "delete_user", user_id: profile.user_id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Usuário excluído com sucesso" });
      setConfirmDelete(null);
      onRefresh?.();
    } catch (err: any) {
      toast({ title: "Erro ao excluir", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const filteredProfiles = profiles.filter((p) =>
    (p.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.phone || "").includes(search)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar usuário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
        <p className="text-xs text-muted-foreground whitespace-nowrap">{filteredProfiles.length} usuários</p>
      </div>

      {filteredProfiles.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
          <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>Nenhum usuário encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left p-4 font-medium">Nome</th>
                  <th className="text-left p-4 font-medium hidden md:table-cell">Telefone</th>
                  <th className="text-left p-4 font-medium">Papéis</th>
                  <th className="text-left p-4 font-medium hidden sm:table-cell">Cadastro</th>
                  <th className="text-left p-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.map((p) => {
                  const roles = userRoles[p.user_id] || [];
                  const isSuperAdmin = roles.includes("super_admin");
                  return (
                    <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="p-4">
                        <p className="text-sm font-medium">{p.full_name || "—"}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{p.user_id?.slice(0, 8)}</p>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">{p.phone || "—"}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {roles.length === 0 ? (
                            <Badge variant="outline" className="text-[10px]">Sem papel</Badge>
                          ) : (
                            roles.map((role) => (
                              <Badge key={role} className={`text-[10px] border ${roleLabels[role]?.color || "bg-muted text-muted-foreground"}`}>
                                {roleLabels[role]?.label || role}
                              </Badge>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground hidden sm:table-cell">
                        {new Date(p.created_at).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(p)}
                            className="p-1.5 rounded-lg hover:bg-primary/10"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4 text-primary" />
                          </button>
                          {!isSuperAdmin && (
                            <button
                              onClick={() => setConfirmDelete(p)}
                              disabled={deleting === p.user_id}
                              className="p-1.5 rounded-lg hover:bg-destructive/10 disabled:opacity-50"
                              title="Excluir"
                            >
                              {deleting === p.user_id ? (
                                <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                              ) : (
                                <Trash2 className="h-4 w-4 text-destructive" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Editar Usuário</DialogTitle>
            <DialogDescription>Altere os dados do usuário abaixo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome completo</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                className="bg-card border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-card border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                placeholder="Novo e-mail (opcional)"
                className="bg-card border-border"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingUser(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-destructive">Excluir Usuário</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir permanentemente <span className="font-semibold text-foreground">{confirmDelete?.full_name || "este usuário"}</span>?
              Todos os restaurantes e dados relacionados serão removidos. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={!!deleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              disabled={!!deleting}
              className="gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Excluir Permanentemente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersTab;
